import React from 'react'
import { Tooltip } from '@mui/material'
import { Play, Minus, Check, X as XIcon } from 'lucide-react'
import { StudentTestCell } from '../TestCaseIO'
import '../../workspace/css/workspace.scss'
import '../../../css/studentview.scss'
import '../../../css/studentview-workspace.scss'

// The /workspace/:id layout for a code (non-GUI) session - what Python / Vizmental students
// get - as a slot-based shell, reproducing Workspace.jsx's DOM and ws- classes pane for pane:
//
//   left  (450px)  Task Description  +  Test Cases (omitted when `tests` is null)
//   middle         code editor, with the 250px terminal pinned to its bottom
//   right (450px)  AI chat - omitted entirely when `chat` is null, as Workspace does when
//                  chat is off (a code session has no Live Preview to keep the column)
//
// Like VizmentalShell, callers fill the slots: the Session Setting tab passes read-only
// replicas with Edit buttons, and the live preview can pass working leaves. No navbar.
//
// Resizers are drawn at Workspace's positions but are inert unless their handler is passed
// (onResizeLeft / onResizeRight / onResizeTerminal, each a mousedown handler, as
// usePanelResize's startDragging*). `contentRef` lands on .ws-content, the box
// usePanelResize measures against. `terminalProps` is spread onto the terminal panel (e.g.
// to mark it as a highlightable region, live/previewRegions.js).
const Resizer = ({ orientation, onMouseDown = null, style }) => (
  <div
    className={`ws-resizer ${orientation}${onMouseDown ? '' : ' sv-inert-resizer'}`}
    style={style}
    onMouseDown={onMouseDown || undefined}
  />
)

const WorkspaceShell = ({
  task,
  tests = null,
  editor,
  runControls,
  terminal,
  chat = null,
  leftWidth = 450,
  rightWidth = 450,
  terminalHeight = 250,
  className = '',
  terminalProps = null,
  contentRef = null,
  onResizeLeft = null,
  onResizeRight = null,
  onResizeTerminal = null,
}) => (
  <div className={`ws-container svw-shell ${className}`}>
    <div className="ws-content" ref={contentRef}>
      <div className="ws-panel-left" style={{ width: `${leftWidth}px` }}>
        <div className="ws-panel-content">
          {task}
          {tests && (
            <>
              <div className="ws-section-divider"></div>
              {tests}
            </>
          )}
        </div>
      </div>

      <Resizer orientation="ws-horizontal" onMouseDown={onResizeLeft} />

      <div className="ws-middle-panel">
        {/* Workspace lets its editor run under the terminal; here the slot stops above it,
            so an edit panel placed in it is never covered. Same look either way. */}
        <div className="svw-editor-slot" style={{ paddingBottom: `${terminalHeight}px` }}>
          {editor}
        </div>
        <div {...terminalProps} className="ws-terminal-panel" style={{ height: `${terminalHeight}px` }}>
          <div className="ws-terminal-controls">
            <div className="ws-controls-left">{runControls}</div>
          </div>
          <div className="ws-terminal-output">
            <div className="ws-output-content">{terminal}</div>
          </div>
        </div>
        <Resizer
          orientation="ws-vertical"
          onMouseDown={onResizeTerminal}
          style={{ position: 'absolute', bottom: terminalHeight, left: 0, right: 0, zIndex: 30 }}
        />
      </div>

      {chat && (
        <>
          <Resizer orientation="ws-horizontal" onMouseDown={onResizeRight} />
          <div
            className="ws-panel-right"
            style={{ width: `${rightWidth}px`, display: 'flex', flexDirection: 'column' }}>
            {chat}
          </div>
        </>
      )}
    </div>
  </div>
)

// ---------------------------------------------------------------- leaves
// Context-free copies of Workspace's leaf markup (TaskDescription, TestCases, Terminal),
// which themselves cannot be mounted outside /workspace: they log telemetry and register
// rrweb regions. `actions` (optional) goes on the right of the section title - the Session
// Setting tab's Edit button. `sectionProps` is spread onto the section.

const SectionTitle = ({ title, actions }) =>
  actions ? (
    <h3 className="ws-section-title sv-section-title-row">
      <span>{title}</span>
      {actions}
    </h3>
  ) : (
    <h3 className="ws-section-title">{title}</h3>
  )

// TaskDescription.jsx: session.task as is - Workspace has no default text, so an empty
// task is an empty box.
export const WorkspaceTaskSection = ({ task, actions = null, sectionProps = null }) => (
  <div {...sectionProps} className="ws-content-section">
    <SectionTitle title="Task Description" actions={actions} />
    <div className="ws-description-box">{task}</div>
  </div>
)

// TestCases.jsx's PassIcon: pending (no result yet), passed, or failed.
export const WorkspacePassIcon = ({ result }) => {
  if (result == null) {
    return (
      <div className="ws-test-icon ws-test-icon-pending">
        <Minus className="ws-icon-md" />
      </div>
    )
  }
  return result ? (
    <div className="ws-test-icon ws-test-icon-passed">
      <Check className="ws-icon-md" />
    </div>
  ) : (
    <div className="ws-test-icon ws-test-icon-failed">
      <XIcon className="ws-icon-md" />
    </div>
  )
}

// TestCases.jsx. `tests` is formatTestsForRender's output (public tests first as "Example
// #N" with input and expected output, each row carrying its `result`), or GUI check rows
// ({ testName, result }) - the same rows Workspace passes. An empty list is an empty table,
// as on /workspace.
export const WorkspaceTestSection = ({ tests = [], actions = null, sectionProps = null }) => (
  <div {...sectionProps} className="ws-content-section">
    <SectionTitle title="Test Cases" actions={actions} />
    <div className="ws-test-table-wrapper">
      <table className="ws-test-table">
        <thead>
          <tr>
            <th>Name</th>
            <th className="ws-result-column">Result</th>
          </tr>
        </thead>
        <tbody>
          {tests.map((test, i) => (
            <tr key={test.index ?? i}>
              <td>{test.view ? <StudentTestCell view={test.view} /> : test.testName}</td>
              <td className="ws-result-column">
                <WorkspacePassIcon result={test.result} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)

// Terminal.jsx's Run button. `onRun` omitted => disabled (instructors do not run code in
// the Session Setting tab). The tooltip hangs off a wrapping span because MUI tooltips do
// not fire on disabled buttons.
export const WorkspaceRunButton = ({ label = 'Run Tests', onRun = null, isRunning = false, tooltip = null }) => {
  const button = (
    <span className="svw-run-controls">
      <button type="button" className="ws-run-button" onClick={onRun || undefined} disabled={!onRun || isRunning}>
        <Play className="ws-icon-sm" />
        {isRunning ? 'Running...' : label}
      </button>
    </span>
  )
  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      {button}
    </Tooltip>
  ) : (
    button
  )
}

// Terminal.jsx's text while its output is empty.
export const workspaceTerminalText = (label = 'Run Tests') => `Click '${label}' to execute your code...`

export default WorkspaceShell
